import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import DineInTableTent from "./DineInTableTent";
import "./styles/DineInPrintSheet.css";

interface DineInPrintSheetProps {
  businessId: string;
  businessName: string;
  businessLogoUrl?: string;
  tableNumbers: number[];
  /** Called once the print dialog has been dismissed, however it was dismissed. */
  onDone: () => void;
}

/**
 * The print job: every table's tent card, laid out for A4.
 *
 * Portalled to document.body and hidden on screen. Printing the tab in place
 * would drag the whole app onto the paper — header, sticky tab bar, the board
 * behind it — so the print stylesheet hides every other direct child of body
 * and reveals only this. Putting the sheet anywhere inside the page tree would
 * make that impossible, since its ancestors would have to stay visible for it
 * to render at all.
 *
 * Mounted only while printing, rather than sitting hidden in the DOM forever:
 * a dozen tables means two dozen 256px canvases, which is real work to do on
 * every render of a screen nobody is printing from most of the time.
 */
const DineInPrintSheet: React.FC<DineInPrintSheetProps> = ({
  businessId,
  businessName,
  businessLogoUrl,
  tableNumbers,
  onDone,
}) => {
  const sheetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let cancelled = false;

    /**
     * Two different things have to be on the page before printing, and they
     * become ready in different ways:
     *
     *  - the QR canvases are drawn by qrcode.react inside an effect, so they
     *    are guaranteed painted after two animation frames. No timer needed.
     *  - the business logos are <img> elements, whose decode has nothing to do
     *    with React's commit. These are the genuinely async ones.
     *
     * This used to be a flat setTimeout(150) covering both, which was a guess:
     * fast enough to look fine locally, and short enough to print a sheet of
     * blank squares or missing logos on a slow device with many tables — with
     * nothing surfaced to say so. The paper is the error message there.
     */
    const imagesSettled = () => {
      const images = Array.from(sheetRef.current?.querySelectorAll("img") ?? []);
      return Promise.all(
        images.map(
          (img) =>
            img.complete
              ? Promise.resolve()
              : new Promise<void>((resolve) => {
                  // error resolves too: a logo that fails is hidden by the
                  // tent's own handler, and a missing logo must never be the
                  // reason a whole sheet won't print.
                  img.addEventListener("load", () => resolve(), { once: true });
                  img.addEventListener("error", () => resolve(), { once: true });
                })
        )
      );
    };

    // Backstop for an image that neither loads nor errors — a hung request
    // would otherwise leave the user staring at a button that did nothing.
    const withTimeout = (p: Promise<unknown>, ms: number) =>
      Promise.race([p, new Promise((resolve) => setTimeout(resolve, ms))]);

    void withTimeout(imagesSettled(), 4000).then(() => {
      if (cancelled) return;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!cancelled) window.print();
        });
      });
    });

    // Fires after the dialog closes whether the user printed or cancelled,
    // which is what we want: either way we're done and the sheet unmounts.
    const handleAfterPrint = () => onDone();
    window.addEventListener("afterprint", handleAfterPrint);

    return () => {
      cancelled = true;
      window.removeEventListener("afterprint", handleAfterPrint);
    };
  }, [onDone]);

  return createPortal(
    <div className="dine-in-print-portal" ref={sheetRef}>
      <div className="dine-in-print-sheet">
        {tableNumbers.map((tableNumber) => (
          <DineInTableTent
            key={tableNumber}
            businessId={businessId}
            businessName={businessName}
            businessLogoUrl={businessLogoUrl}
            tableNumber={tableNumber}
          />
        ))}
      </div>
    </div>,
    document.body
  );
};

export default DineInPrintSheet;
